import { useEffect, useRef, useState } from 'react';
import type { DesktopPolicyState, WorkerPolicy } from './desktop-types';
import { bodyOf, draftOf, problemsOf, sameSettings, type PolicyDraft } from './policy-draft';
import { changedWords } from './policy-words';
import { usePolicySave, type PolicyApi } from './use-policy-save';

const later = (a: string | null, b: string | null) =>
  a !== null && (b === null || Date.parse(a) > Date.parse(b));

/** The notice for a change made elsewhere while the editor was open. */
const elsewhere = (p: WorkerPolicy | null) =>
  p ? `${changedWords(p)} while you had this open. Showing the new settings.` : null;

/** The newest value, for code that runs after a render (an effect, or a save's end), without following it. */
function useLatest<T>(value: T) {
  const latest = useRef(value);
  useEffect(() => {
    latest.current = value;
  });
  return latest;
}

/**
 * The copy the editor's next save is based on, and following the machine's policy as it changes: a newer copy made
 * elsewhere (on the PC, or by another staff member) replaces the draft with a notice; one this editor saved itself is
 * taken quietly. Nothing is followed while a save is out (its own copy can reach the screen before its answer), nor
 * backwards (the screen's read can be older than the editor's own).
 */
function useBasis(state: DesktopPolicyState) {
  const [draft, setDraft] = useState(() => draftOf(state));
  const [saved, setSaved] = useState(draft);
  const [notice, setNotice] = useState<string | null>(null);
  const basedOn = useRef(state.policy?.changedAt ?? null);
  const latest = useLatest(state);
  const saving = useRef(false);
  const adopt = (changedAt: string | null, d: PolicyDraft, words: string | null) => {
    basedOn.current = changedAt;
    setDraft(d);
    setSaved(d);
    setNotice(words);
  };
  const follow = (fresh: DesktopPolicyState) => {
    const at = fresh.policy?.changedAt ?? null;
    if (later(at, basedOn.current)) adopt(at, draftOf(fresh), elsewhere(fresh.policy));
  };
  useEffect(() => {
    if (!saving.current) follow(latest.current);
    // Follows the machine's policy as it changes, not each render's copy of the state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.policy?.changedAt]);
  /** Runs a save: no notice and no following while it is out, then whatever the screen shows by then. */
  const during = async (work: () => Promise<void>) => {
    saving.current = true;
    setNotice(null);
    try {
      await work();
    } catch {
      // The read after a failed save failed too: the screen's next read brings the policy.
    } finally {
      saving.current = false;
      follow(latest.current);
    }
  };
  const basis = () => basedOn.current;
  return { draft, setDraft, saved, notice, adopt, follow, during, basis };
}

/**
 * The Cards and schedule editor's state: the draft and the settings last saved, a notice when the policy changed
 * elsewhere, and the save. A save that failed is read back: when the ledger holds this very draft (a relay that refused
 * it answers 502 after saving), it is this editor's own copy, not someone else's change.
 */
export function usePolicyEditor(state: DesktopPolicyState, api: PolicyApi) {
  const b = useBasis(state);
  const s = usePolicySave(state.machine, api);
  /** After a failed save: this very draft in the ledger, newer than the copy it was based on, is its own. */
  const ownIfSaved = (fresh: DesktopPolicyState, d: PolicyDraft) => {
    const at = fresh.policy?.changedAt ?? null;
    if (sameSettings(draftOf(fresh), d) && later(at, b.basis())) b.adopt(at, d, null);
    else b.follow(fresh);
  };
  const save = () =>
    b.during(async () => {
      const draft = b.draft;
      const done = await s.save(bodyOf(draft, b.basis()));
      if (done.phase === 'changed') b.follow(await api.read(state.machine));
      else if (done.changedAt) b.adopt(done.changedAt, draft, null);
      else if (done.phase === 'failed') ownIfSaved(await api.read(state.machine), draft);
    });
  const busy = s.phase === 'saving' || s.phase === 'confirming';
  return {
    draft: b.draft,
    setDraft: b.setDraft,
    notice: b.notice,
    s,
    save,
    busy,
    locked: !state.changeable || s.phase === 'too-old',
    unchanged: sameSettings(b.draft, b.saved) && !state.problem,
    problems: problemsOf(b.draft),
  };
}

export type PolicyEditorState = ReturnType<typeof usePolicyEditor>;
